import { ChartBarHorizontal, Kanban, ListBullets } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import { Link, useSearch } from "@tanstack/react-router";
import { useRef } from "react";
import { segmentItem, segmentItemActive, segmentTrack } from "@/components/ui/segmented";
import { DisplayOptions as ViewDisplayOptions } from "@/components/views/DisplayOptions";
import { cx } from "@/lib/cx";
import type { GroupBy } from "./grouping";
import type { ViewSearch } from "./viewSearch";

export { sortLabel } from "@/components/views/DisplayOptions";

type Layout = "list" | "board" | "timeline";

const ROUTES = {
  list: "/p/$productKey/list",
  board: "/p/$productKey/board",
  timeline: "/p/$productKey/timeline",
} as const;

/**
 * List / Board / Timeline switch for a product. Links (not tabs): each layout has its own URL and
 * keeps the filter, grouping, sort and the loaded view in the query string.
 */
export function LayoutSwitch({
  productKey,
  current,
  search,
}: {
  productKey: string;
  current: Layout;
  search: ViewSearch;
}) {
  const item = (to: Layout, label: string, Icon: typeof Kanban) => (
    <Link
      to={ROUTES[to]}
      params={{ productKey }}
      search={{ ...search, peek: undefined }}
      aria-current={current === to ? "page" : undefined}
      className={cx(segmentItem, "px-2", current === to && segmentItemActive)}
    >
      <Icon size={14} weight={current === to ? "fill" : "regular"} aria-hidden="true" />
      <span className="max-md:sr-only">{label}</span>
    </Link>
  );
  return (
    <nav aria-label={m.layout_label()} className={segmentTrack}>
      {item("list", m.layout_list(), ListBullets)}
      {item("board", m.layout_board(), Kanban)}
      {item("timeline", m.layout_timeline(), ChartBarHorizontal)}
    </nav>
  );
}

/**
 * Display options for views that are not product views (My work, Triage). The grouping and sort
 * they render with when the URL names none are their defaults.
 */
export function DisplayOptions({
  groupBy,
  sort,
  layout,
  allowProduct,
}: {
  groupBy: GroupBy;
  sort: string;
  layout: "list" | "board";
  allowProduct?: boolean;
}) {
  const search = useSearch({ strict: false }) as ViewSearch;
  const defaults = useRef({ group: groupBy, sort });
  if (!search.group) defaults.current.group = groupBy;
  if (!search.sort) defaults.current.sort = sort;
  return (
    <ViewDisplayOptions
      layout={layout}
      search={search}
      defaultGroup={defaults.current.group}
      defaultSort={defaults.current.sort}
      allowProduct={allowProduct}
    />
  );
}
